import type { AnalyticsEventName, AnalyticsProperties } from './events';

/**
 * The fan-out seam behind `track()`.
 *
 * `track()` is called from ~60 sites and used to be a thin wrapper over
 * `posthog.capture`. Adding a second destination (HubSpot) by editing those call
 * sites would have been ~60 diffs and a permanent invitation to forget one, so
 * the destinations became a registry instead: call sites keep calling `track()`,
 * and each sink decides for itself whether it wants the event.
 *
 * Sinks are registered in src/index.tsx rather than at module scope here, which
 * keeps this module free of SDK imports and lets tests inject fakes with plain
 * `registerSink` calls instead of `vi.doMock` juggling.
 */

export interface TrackOptions {
  /**
   * Set when the very next statement navigates away from the page.
   *
   * posthog-js batches events on a ~3s timer. A full-page navigation fired
   * immediately after `capture()` unloads the document long before that flush,
   * and the event is simply lost — which is what happened to
   * `auth_login_initiated` on every auto-redirect into WorkOS, since the page
   * lives for roughly 100ms. sendBeacon is built for exactly this: the browser
   * takes ownership of the request and delivers it after the document is gone.
   *
   * Sink-specific: only the PostHog sink can act on this. HubSpot's `_hsq`
   * queue has no equivalent transport hint, so its sink ignores the flag.
   */
  beforeNavigation?: boolean;
}

export interface AnalyticsSink {
  /** Stable identifier, used for dedupe on register and in dev warnings. */
  name: string;
  /**
   * Re-evaluated on every event, not cached at registration: consent can be
   * withdrawn mid-session, and for HubSpot this is what makes the script's
   * presence the live consent gate.
   */
  isEnabled(): boolean;
  capture(
    event: AnalyticsEventName,
    properties: AnalyticsProperties | undefined,
    options: TrackOptions | undefined
  ): void;
}

const sinks: AnalyticsSink[] = [];

/**
 * Idempotent by `name` — src/index.tsx runs once, but React StrictMode and the
 * test suite both re-enter module code, and a double registration would double
 * every event.
 */
export const registerSink = (sink: AnalyticsSink): void => {
  if (sinks.some((existing) => existing.name === sink.name)) return;
  sinks.push(sink);
};

export const getSinks = (): readonly AnalyticsSink[] => sinks;

/** Test hook, mirroring `resetAnalyticsContext()` in ./context. */
export const resetSinks = (): void => {
  sinks.length = 0;
};
